import { Input, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useChannelContext } from "@sendbird/uikit-react/Channel/context";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { MESSAGE_INPUT_SPACING } from "../../Chat/constants";
import { isWorkplaceGroupChannel } from "../../Chat/utils";
import { useToast } from "../../lib";
import { IconButton } from "../components/IconButton";
import { MY_SHIFTS_FULL_PATH } from "../WorkerShifts/path";
import {
  type CancellationAlertAction,
  CancellationAlertModal,
} from "./components/CancellationAlertModal";
import {
  useEditingMessage,
  useQuoteMessage,
  useSetEditingMessage,
  useSetQuoteMessage,
} from "./context/EditMessageContext";
import { EditingMessageIndicator } from "./EditingMessageIndicator";
import { useCancellationDetection } from "./hooks/useCancellationDetection";
import { QuoteReply } from "./QuoteReply";

interface Props {
  prefilledMessage?: string;
}

export function CustomMessageInput({ prefilledMessage }: Props) {
  const {
    sendMessage,
    sendFileMessage,
    updateMessage,
    currentGroupChannel,
    setQuoteMessage: setSendbirdQuoteMessage,
  } = useChannelContext();
  const editingMessage = useEditingMessage();
  const setEditingMessage = useSetEditingMessage();
  const quoteMessage = useQuoteMessage();
  const setQuoteMessage = useSetQuoteMessage();

  const [messageInput, setMessageInput] = useState(prefilledMessage ?? "");
  const { showErrorToast } = useToast();
  const { checkMessage, logAction } = useCancellationDetection();
  const cancellationDetectedModal = useModalState();
  const history = useHistory();

  const isEditing = isDefined(editingMessage);

  useEffect(() => {
    if (isDefined(editingMessage)) {
      setMessageInput(editingMessage.message);
    }
  }, [editingMessage]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  const messageIsEmpty = messageInput.trim().length === 0;

  const hideKeyboard = () => {
    textInputRef.current?.blur();
  };

  const executeSendMessage = (message: string) => {
    try {
      sendMessage({ message, ...(isDefined(quoteMessage) && { quoteMessage }) });
      setMessageInput("");
      void currentGroupChannel?.endTyping?.();
      setQuoteMessage(null);
      setSendbirdQuoteMessage(null);
    } catch {
      showErrorToast("There was an error sending your message. Please try again later.");
    }
  };

  const handleSendMessage = () => {
    if (messageIsEmpty) {
      return;
    }

    if (isEditing) {
      try {
        updateMessage({ messageId: editingMessage.messageId, message: messageInput });
        setEditingMessage(null);
        setMessageInput("");
        void currentGroupChannel?.endTyping?.();
      } catch {
        showErrorToast("There was an error updating your message. Please try again later.");
      }
    } else {
      // Check for cancellation requests (hook logs analytics if detected).
      if (checkMessage(messageInput)) {
        void currentGroupChannel?.endTyping?.();
        cancellationDetectedModal.openModal();
        return;
      }
      executeSendMessage(messageInput);
    }
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setMessageInput("");
  };

  const handleCancellationAlertAction = (action: CancellationAlertAction) => {
    logAction(action, messageInput);
    cancellationDetectedModal.closeModal();

    switch (action) {
      case "send": {
        executeSendMessage(messageInput);
        break;
      }
      case "cancel": {
        setMessageInput("");
        break;
      }
      case "goToBookings": {
        setMessageInput("");
        history.push(MY_SHIFTS_FULL_PATH);
        break;
      }
      // No default
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const { files } = event.target;

      if (isDefined(files) && files.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        const file = files[0];
        await sendFileMessage(file);
      }
    } catch {
      showErrorToast("There was an error sending your file. Please try again later.");
    }
  };

  const handleClickAttachFile = () => {
    fileInputRef.current?.click();
  };

  const handleInputFocus = () => {
    const chatContainer = document.querySelector<HTMLElement>(
      ".chat-container .sendbird-conversation__messages-padding"
    );
    chatContainer?.addEventListener("touchmove", hideKeyboard, { passive: true, once: true });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    void currentGroupChannel?.startTyping?.();
    setMessageInput(event.target.value);
  };

  const handleCloseQuoteReply = () => {
    setQuoteMessage(null);
    setSendbirdQuoteMessage(null);
  };

  const isWorkplaceGroupChat =
    isDefined(currentGroupChannel?.url) && isWorkplaceGroupChannel(currentGroupChannel?.url);

  return (
    <>
      <Stack paddingX={MESSAGE_INPUT_SPACING} sx={{ boxShadow: (theme) => theme.shadows[4] }}>
        {isEditing && <EditingMessageIndicator onCancel={handleCancelEdit} />}
        {!isEditing && isDefined(quoteMessage) && (
          <QuoteReply
            quoteMessage={quoteMessage}
            isWorkplaceGroupChat={isWorkplaceGroupChat}
            onClose={handleCloseQuoteReply}
          />
        )}

        <Stack spacing={2} direction="row" alignItems="center" paddingTop={MESSAGE_INPUT_SPACING}>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            aria-label="file-input"
            onChange={handleFileSelect}
          />
          <Input
            inputRef={textInputRef}
            placeholder={isEditing ? "Edit message" : "Message"}
            value={messageInput}
            aria-label="chat-text-input"
            onEnterPress={handleSendMessage}
            onFocus={handleInputFocus}
            onChange={handleInputChange}
          />
          <IconButton
            iconType="paperclip"
            variant="outlined"
            aria-label="attach-file"
            onClick={handleClickAttachFile}
          />
          <IconButton
            iconType="arrow-up"
            variant="contained"
            aria-label="send"
            disabled={messageIsEmpty}
            onClick={handleSendMessage}
          />
        </Stack>
      </Stack>

      {cancellationDetectedModal.modalIsOpen && (
        <CancellationAlertModal
          modalState={cancellationDetectedModal}
          onAction={handleCancellationAlertAction}
        />
      )}
    </>
  );
}
